import { ChangeEvent, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  IconClose,
  IconExpand,
  InputSize,
  InputText,
  Select,
  SelectSize,
  SelectType,
} from '@ui/src';
import { AccountsCurrenciesFormValues } from '@clients/hoc/new-application/types';
import { useAppSelector } from '@store';
import { CustomerStatus, Questionnarie } from '../../../hoc/customers/types';
import AmlGeneralInfoRow from '../../partial/aml/general-info-row';
import AdditionalInfoBlock from './additional-info-block';
import Question from '../../partial/aml/question';
import AdditionalInfoWrapper from '../../partial/aml/additional-info-wrapper';
import { AMLAdditionalInfoTypes } from '../types';
import RegulatedCompanyRow from '../../corporate/aml/parts/row';
import ProfessionalInformation from './parts/professional-information';
import InformationAboutGroup from './parts/information-about-group';
import SourcesOfIncome from './parts/sources-of-income';
import SourcesOfWealth from './parts/sources-of-wealth';
import AccountsCurrencies from '../../corporate/aml/parts/accounts-currencies';
import SourceOfFunds from './parts/source-of-funds';
import AccountsFlows from '../../corporate/aml/parts/accounts-flows';
import ExpectedOriginFunds from '../../corporate/aml/parts/expected-origin-funds';
import Pep from '../../corporate/aml/parts/pep';
import CorrespondenceAddress from './parts/correspondence-address';
import SecondPassport from './parts/second-passport';
import TaxResidence from './parts/tax-residence';
import AffilatedCompanies from './parts/affilated-companies';

import cn from './aml.module.css';

let emailsTimeout: NodeJS.Timeout;

const AdditionalInfo = ({
  questionnaire,
  control,
  setValue,
  currentApplicationScheme,
  questionnaireFields,
  isDirty,
  onResetQuestionnaire,
  onUpdateQuestionnaire,
  requestInProgress,
  status,
  errors,
  clearErrors,
  countries,
  baseCurrency,
  accountsCurrenciesIsLoading,
  accountsCurrenciesIsFetching,
  accountsCurrenciesValues,
  setAccountsCurrenciesValues,
  currencies,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  setAccountsFlowsValues,
  accountsFlowsValues,
  accountsFlowsIsLoading,
  accountsFlowsIsFetching,
  currenciesList,
  expectedFundsOriginsIsLoading,
  expectedFundsOriginsIsFetching,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOriginsValues,
  setExpectedFundsOriginsValues,
  createAccountsCurrenciesIsProgress,
  updateAccountsCurrenciesInProgress,
  createAccountsFlowsIsProgress,
  updateAccountsFlowsInProgress,
  createExpectedFundsOriginsInProgress,
  updateExpectedFundsOriginsInProgress,
  sourceOfWealth,
  sourceOfIncome,
  sourceOfWealthForIndividual,
  customerSourceOfFunds,
  selectedCrsAndFatcaId,
  crsAndFatcaIsLoading,
  crsAndFatcaIsFetching,
  createCrsAndFatcaIsProgress,
  updateCrsAndFatcaInProgress,
  deleteCrsAndFatcaInProgress,
  onDeleteCRSAndFatca,
  organizationControl,
  organizationErrors,
  organizationClearErrors,
  organizationFields,
  organizationSetValue,
  crsAndFatcaValues,
  setCrsAndFatcaValues,
  isCof,
  affilatedCompaniesValues,
  setAffilatedCompaniesValues,
  selectedAffilatedCompanyId,
  affilatedCompaniesIsLoading,
  affilatedCompaniesIsFetching,
  createAffilatedCompanyInProgress,
  updateAffilatedCompanyInProgress,
  deleteAffilatedCompanyInProgress,
  onDeleteAffilatedCompany,
}: AMLAdditionalInfoTypes) => {
  const { settings } = useAppSelector((state) => state.root);
  const [booleanQuestions, setBooleanQuestions] = useState<
    Record<string, boolean>
  >({});
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'onlyYes'>(
    'all',
  );
  const [additionalEmails, setAdditionalEmails] = useState<
    { id: string; email: string }[]
  >([
    {
      id: `email-${uuid()}`,
      email: '',
    },
  ]);

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    clearTimeout(emailsTimeout);

    const nextEmails = additionalEmails?.map((item) => {
      const result = { ...item };

      if (e.target.name === `email-${result.id}`) {
        result.email = e.target.value;
      }

      return result;
    });

    emailsTimeout = setTimeout(() => {
      setValue(
        'emails',
        nextEmails?.filter((item) => item.email)?.map((item) => item.email),
      );
    }, 700);

    setAdditionalEmails(nextEmails);
  };

  const onDeleteAdditionalEmails = (id: string) => {
    const nextEmails = additionalEmails?.filter((item) => item.id !== id);

    setAdditionalEmails(nextEmails);
    setValue(
      'emails',
      nextEmails?.filter((item) => item.email)?.map((item) => item.email),
    );
  };

  useEffect(() => {
    if (questionnaire?.emails?.length) {
      setAdditionalEmails(
        questionnaire?.emails?.map((item) => ({
          id: `email-${uuid()}`,
          email: item,
        })),
      );
    }
  }, [questionnaire]);

  const uboPercentage = settings?.find(
    (item) => item.key === 'MINIMUM-UBO-OWNERSHIP-PERCENTAGE',
  )?.data?.value;

  useEffect(() => {
    if (questionnaire) {
      const questions: Record<string, boolean> = {};

      Object.keys(questionnaire).forEach((item) => {
        if (selectedFilter === 'all') {
          if (
            questionnaire[item as keyof Questionnarie] !== null &&
            typeof questionnaire[item as keyof Questionnarie] === 'boolean'
          ) {
            questions[item] = questionnaire[
              item as keyof Questionnarie
            ] as boolean;
          }
        } else if (selectedFilter === 'onlyYes') {
          if (
            questionnaire[item as keyof Questionnarie] &&
            typeof questionnaire[item as keyof Questionnarie] === 'boolean'
          ) {
            questions[item] = questionnaire[
              item as keyof Questionnarie
            ] as boolean;
          }
        }
      });

      setBooleanQuestions(questions);
    }
  }, [questionnaire, selectedFilter]);

  return (
    <AdditionalInfoWrapper>
      <>
        {(currentApplicationScheme?.id ===
          'ed560559-8354-4634-b6dd-eb341dfb551a' ||
          currentApplicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159') && (
          <>
            <AdditionalInfoBlock title="" withoutTitle>
              <RegulatedCompanyRow title="Place of birth">
                <div className={cn.placeOfBirthWrapper}>
                  <div className={cn.placeOfBirthRow}>
                    <Controller
                      control={organizationControl}
                      name="birthCountryId"
                      defaultValue=""
                      render={({ field: { onChange, value, name } }) => (
                        <Autocomplete
                          name={name}
                          placeholder="Select country..."
                          list={countries || []}
                          value={value}
                          selectWrapperClassName={
                            status === CustomerStatus.CLOSED
                              ? cn.selectDisabled
                              : ''
                          }
                          error={organizationErrors?.birthCountryId?.message}
                          size={SelectSize.SMALL}
                          onChange={(e) => {
                            onChange(e);
                            if (organizationClearErrors) {
                              organizationClearErrors(['birthCountryId']);
                            }
                          }}
                          borderless
                          tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                          disabled={status === CustomerStatus.CLOSED}
                          fullWidth
                        />
                      )}
                    />
                  </div>
                  <div className={cn.placeOfBirthRow}>
                    <Controller
                      control={organizationControl}
                      name="placeOfBirth"
                      defaultValue=""
                      render={({ field: { onChange, value, name } }) => (
                        <InputText
                          name={name}
                          placeholder="Enter city.."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            if (organizationClearErrors) {
                              organizationClearErrors(['placeOfBirth']);
                            }
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          error={organizationErrors?.placeOfBirth?.message}
                          tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                          disabled={status === CustomerStatus.CLOSED}
                        />
                      )}
                    />
                  </div>
                </div>
              </RegulatedCompanyRow>
              <RegulatedCompanyRow title="Other nationalities">
                <Controller
                  control={control}
                  name="otherNationalitiesIds"
                  defaultValue={[]}
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select country.."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['otherNationalitiesIds']);
                      }}
                      list={countries}
                      size={SelectSize.SMALL}
                      className="borderless"
                      disabled={status === CustomerStatus.CLOSED}
                      tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                      error={errors?.otherNationalitiesIds?.message}
                      mode={SelectType.MULTIPLE}
                      borderless
                    />
                  )}
                />
              </RegulatedCompanyRow>
              <RegulatedCompanyRow title="Marital Status">
                <Controller
                  control={control}
                  name="maritalStatus"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Enter status..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['maritalStatus']);
                      }}
                      list={[
                        {
                          name: 'Single',
                          value: 'SINGLE',
                        },
                        {
                          name: 'Divorced',
                          value: 'DIVORCED',
                        },
                        {
                          name: 'Married',
                          value: 'MARRIED',
                        },
                      ]}
                      size={SelectSize.SMALL}
                      className="borderless"
                      disabled={status === CustomerStatus.CLOSED}
                      tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                      error={errors?.maritalStatus?.message}
                      borderless
                    />
                  )}
                />
              </RegulatedCompanyRow>
              {questionnaireFields?.maritalStatus === 'MARRIED' && (
                <RegulatedCompanyRow title="Marital Status">
                  <Controller
                    control={control}
                    name="spouseFullName"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter full name..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['spouseFullName']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        disabled={status === CustomerStatus.CLOSED}
                        tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                        error={errors?.maritalStatus?.message}
                      />
                    )}
                  />
                </RegulatedCompanyRow>
              )}
              {!isCof && (
                <>
                  <RegulatedCompanyRow title="Home phone number">
                    <Controller
                      control={control}
                      name="homePhoneNumber"
                      defaultValue=""
                      render={({ field: { onChange, value, name } }) => (
                        <InputText
                          name={name}
                          placeholder="Enter Phone number..."
                          value={value}
                          onChange={(e) => {
                            onChange(e);
                            clearErrors(['homePhoneNumber']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          disabled={status === CustomerStatus.CLOSED}
                          tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                          error={errors?.homePhoneNumber?.message}
                        />
                      )}
                    />
                  </RegulatedCompanyRow>
                  {additionalEmails?.map((item, index) => (
                    <RegulatedCompanyRow
                      title={`Email #${index + 1}`}
                      key={item.id}
                    >
                      <div className={cn.additionalEmailWrapper}>
                        <InputText
                          name={`email-${item.id}`}
                          placeholder="Enter email..."
                          value={item.email}
                          onChange={(e) => {
                            onChangeEmail({
                              target: {
                                name: `email-${item.id}`,
                                value: e.target.value,
                              },
                            } as ChangeEvent<HTMLInputElement>);
                            clearErrors(['emails']);
                          }}
                          size={InputSize.SMALL}
                          className="borderless"
                          tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                          disabled={status === CustomerStatus.CLOSED}
                        />
                        {index > 0 && (
                          <button
                            type="button"
                            className={cn.removeEmailButton}
                            onClick={() => onDeleteAdditionalEmails(item.id)}
                          >
                            <IconClose width={16} height={16} />
                          </button>
                        )}
                      </div>
                    </RegulatedCompanyRow>
                  ))}
                  <div className={cn.addEmailButtonWrapper}>
                    <ButtonSecondary
                      text="Add email"
                      icon={<IconExpand width={16} height={16} />}
                      size={ButtonSize.SMALL}
                      className={`button-white ${cn.addEmailButton}`}
                      onClick={() => {
                        const nextEmails = (
                          [] as {
                            id: string;
                            email: string;
                          }[]
                        ).concat(additionalEmails);

                        nextEmails.push({
                          id: `email-${uuid()}`,
                          email: '',
                        });

                        setAdditionalEmails(nextEmails);
                      }}
                    />
                  </div>
                </>
              )}
            </AdditionalInfoBlock>
            <ProfessionalInformation
              organizationControl={organizationControl}
              organizationErrors={organizationErrors}
              organizationClearErrors={organizationClearErrors}
              control={control}
              errors={errors}
              clearErrors={clearErrors}
              countries={countries}
              status={status}
              organizationFields={organizationFields}
            />
            {isCof && (
              <AffilatedCompanies
                control={control}
                clearErrors={clearErrors}
                errors={errors}
                status={status}
                fields={questionnaireFields}
                affilatedCompaniesValues={affilatedCompaniesValues}
                setAffilatedCompaniesValues={setAffilatedCompaniesValues}
                affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
                affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
                deleteAffilatedCompanyInProgress={
                  deleteAffilatedCompanyInProgress
                }
                onDeleteAffilatedCompany={onDeleteAffilatedCompany}
                selectedAffilatedCompanyId={selectedAffilatedCompanyId}
                uboPercentage={uboPercentage as string}
                countries={countries}
              />
            )}
            <InformationAboutGroup
              control={control}
              clearErrors={clearErrors}
              errors={errors}
              status={status}
              countries={countries}
              sourceOfWealth={sourceOfWealth}
              baseCurrency={baseCurrency}
              setValue={setValue}
              fields={questionnaireFields}
              isCof={isCof}
            />
            <SourcesOfIncome
              sourcesOfIncome={sourceOfIncome}
              fields={questionnaireFields}
              control={control}
              clearErrors={clearErrors}
              setValue={setValue}
              status={status}
              errors={errors}
              baseCurrency={baseCurrency}
            />
            <SourcesOfWealth
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              control={control}
              clearErrors={clearErrors}
              errors={errors}
              status={status}
              setValue={setValue}
              fields={questionnaireFields}
              baseCurrency={baseCurrency}
            />
            <AdditionalInfoBlock title="Net worth">
              <RegulatedCompanyRow title="Require">
                <Controller
                  control={control}
                  name="requiresNetWorth"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select an option..."
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        clearErrors(['requiresNetWorth']);

                        if (e.target.value === 'no') {
                          clearErrors(['netWorthAmount']);
                          setValue('netWorthAmount', '');
                        }
                      }}
                      selectWrapperClassName={
                        status === CustomerStatus.CLOSED
                          ? cn.selectDisabled
                          : ''
                      }
                      disabled={status === CustomerStatus.CLOSED}
                      tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                      error={errors?.requiresNetWorth?.message}
                      fullWidth
                      borderless
                    />
                  )}
                />
              </RegulatedCompanyRow>
              {questionnaireFields?.requiresNetWorth === 'yes' && (
                <RegulatedCompanyRow title="Net Worth">
                  <Controller
                    control={control}
                    name="netWorthAmount"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <InputText
                        name={name}
                        placeholder="Enter amount..."
                        value={value}
                        size={InputSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors(['netWorthAmount']);
                        }}
                        className="borderless"
                        disabled={status === CustomerStatus.CLOSED}
                        tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                        error={errors?.netWorthAmount?.message}
                        currency={baseCurrency}
                        amountInput
                      />
                    )}
                  />
                </RegulatedCompanyRow>
              )}
            </AdditionalInfoBlock>
            {!isCof && (
              <>
                <div style={{ position: 'relative', zIndex: 5 }}>
                  <AccountsCurrencies
                    control={control}
                    status={status}
                    errors={errors}
                    clearErrors={clearErrors}
                    accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
                    accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
                    accountsCurrenciesValues={accountsCurrenciesValues}
                    setAccountsCurrenciesValues={
                      setAccountsCurrenciesValues as (
                        data: AccountsCurrenciesFormValues[],
                      ) => void
                    }
                    currencies={currencies}
                    onDeleteAccountCurrencies={onDeleteAccountCurrencies}
                    selectedAccountsCurrencyId={selectedAccountsCurrencyId}
                    deleteAccountsCurrenciesInProgress={
                      deleteAccountsCurrenciesInProgress
                    }
                    setAccountsFlowsValues={setAccountsFlowsValues}
                    accountsFlowsValues={accountsFlowsValues}
                    isIndividual
                  />
                </div>
                <div style={{ position: 'relative', zIndex: 4 }}>
                  <SourceOfFunds
                    control={control}
                    customerSourceOfFunds={customerSourceOfFunds}
                    clearErrors={clearErrors}
                    fields={questionnaireFields}
                    setValue={setValue}
                    status={status}
                    errors={errors}
                  />
                </div>
                <AccountsFlows
                  accountsFlowsValues={accountsFlowsValues}
                  accountsFlowsIsLoading={accountsFlowsIsLoading}
                  accountsFlowsIsFetching={accountsFlowsIsFetching}
                  errors={errors}
                  setAccountsFlowsValues={setAccountsFlowsValues}
                  currenciesList={currenciesList}
                  status={status}
                />
                <ExpectedOriginFunds
                  expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
                  expectedFundsOriginsIsFetching={
                    expectedFundsOriginsIsFetching
                  }
                  accountsFlowsValues={accountsFlowsValues}
                  errors={errors}
                  status={status}
                  accountsCurrenciesValues={accountsCurrenciesValues}
                  currencies={currencies}
                  deleteExpectedFundsOriginsInProgress={
                    deleteExpectedFundsOriginsInProgress
                  }
                  onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
                  selectedExpectedFundsOriginsId={
                    selectedExpectedFundsOriginsId
                  }
                  expectedFundsOriginsValues={expectedFundsOriginsValues}
                  setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
                  countries={countries}
                />
              </>
            )}

            <CorrespondenceAddress
              organizationControl={organizationControl}
              organizationSetValue={organizationSetValue}
              status={status}
              organizationClearErrors={organizationClearErrors}
              countries={countries}
              organizationFields={organizationFields}
            />
            <SecondPassport
              status={status}
              control={control}
              clearErrors={clearErrors}
              errors={errors}
              countries={countries}
              fields={questionnaireFields}
            />
            <Pep
              control={control}
              status={status}
              setValue={setValue}
              errors={errors}
              clearErrors={clearErrors}
              fields={questionnaireFields}
              countries={countries}
              isIndividual
            />
            <TaxResidence
              errors={errors}
              crsAndFatcaIsLoading={crsAndFatcaIsLoading}
              crsAndFatcaIsFetching={crsAndFatcaIsFetching}
              crsAndFatcaValues={crsAndFatcaValues}
              setCrsAndFatcaValues={setCrsAndFatcaValues}
              countries={countries}
              status={status}
              onDeleteCRSAndFatca={onDeleteCRSAndFatca}
              selectedCrsAndFatcaId={selectedCrsAndFatcaId}
              deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
            />
            {!isCof && (
              <AdditionalInfoBlock title="US citizen">
                <RegulatedCompanyRow title="Are you a citizen of U.S.?">
                  <Controller
                    control={control}
                    name="isUSCitizen"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Select
                        name="isUSCitizen"
                        placeholder="Select an option"
                        list={[
                          { name: 'Yes', value: 'yes' },
                          { name: 'No', value: 'no' },
                        ]}
                        value={value as string}
                        selectWrapperClassName={
                          status === CustomerStatus.CLOSED
                            ? cn.selectDisabled
                            : ''
                        }
                        size={SelectSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          clearErrors('isUSCitizen');
                        }}
                        disabled={status === CustomerStatus.CLOSED}
                        tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                        error={errors?.isUSCitizen?.message}
                        fullWidth
                        borderless
                      />
                    )}
                  />
                </RegulatedCompanyRow>
                <RegulatedCompanyRow title="Are you a resident of U.S.?">
                  <Controller
                    control={organizationControl}
                    name="isUSResident"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Select
                        name="isUSResident"
                        placeholder="Select an option"
                        list={[
                          { name: 'Yes', value: 'yes' },
                          { name: 'No', value: 'no' },
                        ]}
                        value={value as string}
                        selectWrapperClassName={
                          status === CustomerStatus.CLOSED
                            ? cn.selectDisabled
                            : ''
                        }
                        size={SelectSize.SMALL}
                        onChange={(e) => {
                          onChange(e);

                          if (organizationClearErrors) {
                            organizationClearErrors('isUSResident');
                          }
                        }}
                        disabled={status === CustomerStatus.CLOSED}
                        tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                        error={organizationErrors?.isUSResident?.message}
                        fullWidth
                        borderless
                      />
                    )}
                  />
                </RegulatedCompanyRow>
              </AdditionalInfoBlock>
            )}
          </>
        )}
        {questionnaire?.nationalInsuranceNumberOfDirectors && (
          <AmlGeneralInfoRow
            title="National Insurance number of directors"
            value={questionnaire?.nationalInsuranceNumberOfDirectors}
            name="nationalInsuranceNumberOfDirectors"
            type="text"
          />
        )}
        {questionnaire?.nationalInsuranceNumberOfDirectors && (
          <AmlGeneralInfoRow
            title="Last 3 years address history"
            value={questionnaire?.lastThreeYearsAddressHistory}
            name="lastThreeYearsAddressHistory"
            type="text"
          />
        )}
        {currentApplicationScheme &&
          currentApplicationScheme?.id !==
            'ed560559-8354-4634-b6dd-eb341dfb551a' &&
          currentApplicationScheme?.id !==
            'a87db4b4-3c78-4564-bba1-c16a19553159' && (
            <>
              <div className={cn.checklistTitle}>Checklist</div>
              <div className={cn.filters}>
                <button
                  type="button"
                  className={`${cn.filterButton} ${
                    selectedFilter === 'all' ? cn.active : ''
                  }`}
                  onClick={() => setSelectedFilter('all')}
                >
                  Show all
                </button>
                <button
                  type="button"
                  className={`${cn.filterButton} ${
                    selectedFilter === 'onlyYes' ? cn.active : ''
                  }`}
                  onClick={() => setSelectedFilter('onlyYes')}
                >
                  Only “Yes”
                </button>
              </div>
            </>
          )}

        {(booleanQuestions?.criminalArrestedOrChargedWithOrBeenInvolved?.toString() ||
          booleanQuestions?.criminalConvictedOfAny?.toString() ||
          booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString() ||
          booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?.toString() ||
          booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString() ||
          booleanQuestions?.criminalReceivedACautionForAny?.toString() ||
          booleanQuestions?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString()) && (
          <AdditionalInfoBlock title="Criminal Proceedings">
            <>
              {booleanQuestions?.criminalReceivedACautionForAny?.toString() && (
                <Question
                  question="Have you ever been convicted of any criminal offence?"
                  answer={
                    booleanQuestions?.criminalReceivedACautionForAny
                      ? 'Yes'
                      : 'No'
                  }
                  active={booleanQuestions?.criminalReceivedACautionForAny}
                  name="criminalReceivedACautionForAny"
                />
              )}
              {booleanQuestions?.criminalConvictedOfAny?.toString() && (
                <Question
                  question="Have you ever received a caution for any criminal offence?"
                  answer={
                    booleanQuestions?.criminalConvictedOfAny ? 'Yes' : 'No'
                  }
                  active={booleanQuestions?.criminalConvictedOfAny}
                  name="criminalConvictedOfAny"
                />
              )}
              {booleanQuestions?.criminalArrestedOrChargedWithOrBeenInvolved?.toString() && (
                <Question
                  question="Have you ever been arrested or charged with any criminal offence or been involved in a criminal investigation?"
                  answer={
                    booleanQuestions?.criminalArrestedOrChargedWithOrBeenInvolved
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.criminalArrestedOrChargedWithOrBeenInvolved
                  }
                  name="criminalArrestedOrChargedWithOrBeenInvolved"
                />
              )}
              {booleanQuestions?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString() && (
                <Question
                  question="Have you ever been subject to any current criminal investigation?"
                  answer={
                    booleanQuestions?.criminalSubjectToAnyCurrentCriminalInvestigation
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.criminalSubjectToAnyCurrentCriminalInvestigation
                  }
                  name="criminalSubjectToAnyCurrentCriminalInvestigation"
                />
              )}
              {booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString() && (
                <Question
                  question="Has any firm where you held a position of influence ever been convicted, charged, or investigated for a criminal offence?"
                  answer={
                    booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted
                  }
                  name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted"
                />
              )}
              {booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString() && (
                <Question
                  question="Has any firm where you held a position of influence ever been ordered to produce documents for or been searched in relation to a criminal investigation?"
                  answer={
                    booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered
                  }
                  name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                />
              )}
              {booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString() && (
                <Question
                  question="Has any firm where you held a position of influence ever been ordered to produce documents for or been searched in relation to a criminal investigation?"
                  answer={
                    booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered
                  }
                  name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                />
              )}
            </>
          </AdditionalInfoBlock>
        )}
        {(booleanQuestions?.civilFiledForBankruptcy?.toString() ||
          booleanQuestions?.civilFoundLiableForFraud?.toString() ||
          booleanQuestions?.civilInvolvedInAnyCivilProceedings?.toString() ||
          booleanQuestions?.civilSubjectToABankruptcy?.toString() ||
          booleanQuestions?.civilSubjectToAJudgmentDebt?.toString() ||
          booleanQuestions?.civilYouHeldAPositionOfInfluence?.toString()) && (
          <AdditionalInfoBlock title="Civil Proceedings">
            <>
              {booleanQuestions?.civilFoundLiableForFraud?.toString() && (
                <Question
                  question="Have you ever been found liable for fraud, negligence, or misconduct by a court?"
                  answer={
                    booleanQuestions?.civilFoundLiableForFraud ? 'Yes' : 'No'
                  }
                  active={booleanQuestions?.civilFoundLiableForFraud}
                  name="civilFoundLiableForFraud"
                />
              )}
              {booleanQuestions?.civilSubjectToAJudgmentDebt?.toString() && (
                <Question
                  question="Have you ever been subject to a judgment debt or award?"
                  answer={
                    booleanQuestions?.civilSubjectToAJudgmentDebt ? 'Yes' : 'No'
                  }
                  active={booleanQuestions?.civilSubjectToAJudgmentDebt}
                  name="civilSubjectToAJudgmentDebt"
                />
              )}
              {booleanQuestions?.civilInvolvedInAnyCivilProceedings?.toString() && (
                <Question
                  question="Have you ever been involved in any civil proceedings that resulted in a finding against you?"
                  answer={
                    booleanQuestions?.civilInvolvedInAnyCivilProceedings
                      ? 'Yes'
                      : 'No'
                  }
                  active={booleanQuestions?.civilInvolvedInAnyCivilProceedings}
                  name="civilInvolvedInAnyCivilProceedings"
                />
              )}
              {booleanQuestions?.civilYouHeldAPositionOfInfluence?.toString() && (
                <Question
                  question="Has any firm where you held a position of influence ever been found liable in civil proceedings for fraud or misconduct?"
                  answer={
                    booleanQuestions?.civilYouHeldAPositionOfInfluence
                      ? 'Yes'
                      : 'No'
                  }
                  active={booleanQuestions?.civilYouHeldAPositionOfInfluence}
                  name="civilYouHeldAPositionOfInfluence"
                />
              )}
              {booleanQuestions?.civilFiledForBankruptcy?.toString() && (
                <Question
                  question="Have you ever filed for bankruptcy or been judged bankrupt?"
                  answer={
                    booleanQuestions?.civilFiledForBankruptcy ? 'Yes' : 'No'
                  }
                  active={booleanQuestions?.civilFiledForBankruptcy}
                  name="civilFiledForBankruptcy"
                />
              )}
              {booleanQuestions?.civilSubjectToABankruptcy?.toString() && (
                <Question
                  question="Have you ever been subject to a bankruptcy restriction order or made any arrangements with creditors?"
                  answer={
                    booleanQuestions?.civilSubjectToABankruptcy ? 'Yes' : 'No'
                  }
                  active={booleanQuestions?.civilSubjectToABankruptcy}
                  name="civilSubjectToABankruptcy"
                />
              )}
            </>
          </AdditionalInfoBlock>
        )}
        {(booleanQuestions?.businessAndEmploymentDismissedOrAskedTo?.toString() ||
          booleanQuestions?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString() ||
          booleanQuestions?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString()) && (
          <AdditionalInfoBlock title="Business and Employment Matters">
            <>
              {booleanQuestions?.businessAndEmploymentDismissedOrAskedTo?.toString() && (
                <Question
                  question="Have you ever been dismissed or asked to resign from a position?"
                  answer={
                    booleanQuestions?.businessAndEmploymentDismissedOrAskedTo
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.businessAndEmploymentDismissedOrAskedTo
                  }
                  name="businessAndEmploymentDismissedOrAskedTo"
                />
              )}
              {booleanQuestions?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString() && (
                <Question
                  question="Have you ever been disqualified from acting as a director?"
                  answer={
                    booleanQuestions?.businessAndEmploymentDisqualifiedFromActingAsADirector
                      ? 'Yes'
                      : 'No'
                  }
                  active={booleanQuestions?.civilSubjectToAJudgmentDebt}
                  name="civilSubjectToAJudgmentDebt"
                />
              )}
              {booleanQuestions?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString() && (
                <Question
                  question="Have you ever been the subject of any disciplinary proceedings or investigations?"
                  answer={
                    booleanQuestions?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings
                  }
                  name="businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings"
                />
              )}
            </>
          </AdditionalInfoBlock>
        )}
        {(booleanQuestions?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString() ||
          booleanQuestions?.regulatoryCensured?.toString() ||
          booleanQuestions?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString() ||
          booleanQuestions?.regulatorySubjectToAnyDisqualification?.toString()) && (
          <AdditionalInfoBlock title="Regulatory Matters">
            <>
              {booleanQuestions?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString() && (
                <Question
                  question="Have you ever had an authorization from a regulatory body refused, revoked, or terminated?"
                  answer={
                    booleanQuestions?.regulatoryAnAuthorizationFromARegulatoryBodyRefused
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.businessAndEmploymentDismissedOrAskedTo
                  }
                  name="businessAndEmploymentDismissedOrAskedTo"
                />
              )}
              {booleanQuestions?.regulatoryCensured?.toString() && (
                <Question
                  question="Have you ever been censured, disciplined, or investigated by a regulatory body?"
                  answer={booleanQuestions?.regulatoryCensured ? 'Yes' : 'No'}
                  active={booleanQuestions?.regulatoryCensured}
                  name="regulatoryCensured"
                />
              )}
              {booleanQuestions?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString() && (
                <Question
                  question="Have you ever been involved in any activities requiring authorization that you did not have?"
                  answer={
                    booleanQuestions?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization
                  }
                  name="regulatoryInvolvedInAnyActivitiesRequiringAuthorization"
                />
              )}
              {booleanQuestions?.regulatorySubjectToAnyDisqualification?.toString() && (
                <Question
                  question="Have you ever been subject to any disqualification or prohibition orders under financial regulations?"
                  answer={
                    booleanQuestions?.regulatorySubjectToAnyDisqualification
                      ? 'Yes'
                      : 'No'
                  }
                  active={
                    booleanQuestions?.regulatorySubjectToAnyDisqualification
                  }
                  name="regulatorySubjectToAnyDisqualification"
                />
              )}
            </>
          </AdditionalInfoBlock>
        )}
      </>
      {(control || organizationControl) && status !== CustomerStatus.CLOSED && (
        <div className={cn.controlsWrapper}>
          <ButtonPrimary
            size={ButtonSize.SMALL}
            text={
              requestInProgress ||
              createAccountsCurrenciesIsProgress ||
              updateAccountsCurrenciesInProgress ||
              createAccountsFlowsIsProgress ||
              updateAccountsFlowsInProgress ||
              createExpectedFundsOriginsInProgress ||
              updateExpectedFundsOriginsInProgress ||
              createCrsAndFatcaIsProgress ||
              updateCrsAndFatcaInProgress ||
              createAffilatedCompanyInProgress ||
              updateAffilatedCompanyInProgress
                ? 'Loading...'
                : 'Save'
            }
            className={cn.saveQuestionnaireButton}
            disabled={
              !isDirty ||
              requestInProgress ||
              createAccountsCurrenciesIsProgress ||
              updateAccountsCurrenciesInProgress ||
              createAccountsFlowsIsProgress ||
              updateAccountsFlowsInProgress ||
              createExpectedFundsOriginsInProgress ||
              updateExpectedFundsOriginsInProgress ||
              createCrsAndFatcaIsProgress ||
              updateCrsAndFatcaInProgress ||
              createAffilatedCompanyInProgress ||
              updateAffilatedCompanyInProgress
            }
            onClick={onUpdateQuestionnaire}
            bold
          />
          <ButtonSecondary
            className="button-white"
            size={ButtonSize.SMALL}
            text="Reset"
            disabled={
              !isDirty ||
              requestInProgress ||
              createAccountsCurrenciesIsProgress ||
              updateAccountsCurrenciesInProgress ||
              createAccountsFlowsIsProgress ||
              updateAccountsFlowsInProgress ||
              createExpectedFundsOriginsInProgress ||
              updateExpectedFundsOriginsInProgress ||
              createCrsAndFatcaIsProgress ||
              updateCrsAndFatcaInProgress
            }
            onClick={onResetQuestionnaire}
            bold
          />
        </div>
      )}
    </AdditionalInfoWrapper>
  );
};

export default AdditionalInfo;
